import { createI18n, detectLocale } from '@aj/i18n';
import type { I18n, Locale } from '@aj/i18n';
import { createContext, useContext, useEffect, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';

const STORAGE_KEY = 'aj.locale';

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'bn') return stored;
  } catch {
    /* fall through to device detection */
  }
  return detectLocale(navigator.languages ?? [navigator.language]);
}

/** One runtime for the whole app — locale changes notify subscribers rather
 *  than re-creating formatters, which matters on low-end devices where
 *  `Intl.NumberFormat` construction is not free. */
const i18n = createI18n(initialLocale());

const I18nContext = createContext<I18n>(i18n);

export function I18nProvider({ children }: { children: ReactNode }) {
  // Re-render the tree when the locale changes, without a state duplicate.
  useSyncExternalStore(
    (cb) => i18n.subscribe(cb),
    () => i18n.locale,
    () => i18n.locale,
  );

  useEffect(() => {
    // `lang` drives the :lang(bn) leading rule and the browser's own
    // hyphenation and font fallback for Bengali.
    document.documentElement.setAttribute('lang', i18n.locale);
    try {
      localStorage.setItem(STORAGE_KEY, i18n.locale);
    } catch {
      /* private mode */
    }
  }, [i18n.locale]);

  return <I18nContext.Provider value={i18n}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  return useContext(I18nContext);
}

/** Convenience for the common case. */
export function useT(): I18n['t'] {
  return useContext(I18nContext).t;
}
